import React from 'react';
import { Field, FieldArray, useFormikContext } from 'formik';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { Trash } from 'react-bootstrap-icons';
import PropTypes from 'prop-types';
import { generateWorkerTimesheetFrontendId } from '../../clients/worker_timesheet_client';

/**
 * Generic file attachments with optional captions (index-aligned with `attachments` files on submit).
 *
 * @param {Object} props
 * @param {string} [props.name] - FieldArray name (default `attachments`)
 * @param {number} props.maxFileBytes - Soft size limit per file (bytes)
 * @returns {JSX.Element}
 */
const AttachmentsSection = ({ name = 'attachments', maxFileBytes }) => {
  const { setFieldValue, values, errors, touched } = useFormikContext();
  const list = values[name] || [];
  const fieldError = errors[name];
  const fieldTouched = touched[name];

  return (
    <div className="worker-ts-attachments">
      <h5 className="mb-3">קבצים מצורפים (אופציונלי)</h5>
      <p className="small text-muted">
        עד ~{Math.round(maxFileBytes / (1024 * 1024))} MB לקובץ
      </p>
      <FieldArray name={name}>
        {({ push, remove }) => (
          <>
            {list.map((row, i) => (
              <div key={row.frontend_id || i} className="mb-3">
                <div className="d-flex d-md-none justify-content-between align-items-center mb-2">
                  <span className="text-muted small">קובץ {i + 1}</span>
                  <Button
                    type="button"
                    variant="outline-danger"
                    size="sm"
                    title="הסר קובץ"
                    onClick={() => remove(i)}
                    aria-label="הסר קובץ מצורף"
                  >
                    <Trash size={18} />
                  </Button>
                </div>
                <Row className="g-2 align-items-end">
                <Col md={5} xs={12}>
                  <Form.Label>קובץ</Form.Label>
                  <Form.Control
                    type="file"
                    name={`${name}.${i}.file`}
                    onChange={e => {
                      const f = e.target.files?.[0] || null;
                      setFieldValue(`${name}.${i}.file`, f);
                    }}
                  />
                  {row.file instanceof File && (
                    <div className="small text-muted mt-1">
                      נבחר: {row.file.name} (
                      {(row.file.size / 1024).toFixed(1)} ק״ב)
                    </div>
                  )}
                </Col>
                <Col md={5} xs={12}>
                  <Form.Label>תיאור (אופציונלי)</Form.Label>
                  <Field
                    as={Form.Control}
                    name={`${name}.${i}.caption`}
                    placeholder="תאר את הקובץ"
                  />
                </Col>
                <Col
                  md={2}
                  xs={12}
                  className="d-none d-md-flex justify-content-end align-items-end"
                >
                  <Button
                    type="button"
                    variant="outline-danger"
                    size="sm"
                    title="הסר קובץ"
                    onClick={() => remove(i)}
                    aria-label="הסר קובץ מצורף"
                  >
                    <Trash size={18} />
                  </Button>
                </Col>
              </Row>
              </div>
            ))}
            <div className="d-grid d-sm-inline-block">
            <Button
              type="button"
              variant="outline-secondary"
              size="sm"
              className="py-2"
              onClick={() =>
                push({
                  file: null,
                  caption: '',
                  frontend_id: generateWorkerTimesheetFrontendId(),
                })
              }
            >
              הוסף קובץ
            </Button>
            </div>
          </>
        )}
      </FieldArray>
      {fieldTouched && typeof fieldError === 'string' && (
        <div className="text-danger small mt-2">{fieldError}</div>
      )}
    </div>
  );
};

AttachmentsSection.propTypes = {
  name: PropTypes.string,
  maxFileBytes: PropTypes.number.isRequired,
};

export default AttachmentsSection;
